import React, { useState, useEffect } from 'react';
import { fetchProximityPairs, updateProximityPairStatus, type SkillProximityPair } from '../../lib/referentialService';
import { supabase } from '../../lib/supabase';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  confirmed: { label: 'Confirmé', color: 'bg-green-100 text-green-800' },
  merged: { label: 'Fusionné', color: 'bg-blue-100 text-blue-800' },
  dismissed: { label: 'Ignoré', color: 'bg-gray-100 text-gray-600' },
};

const SkillProximity: React.FC = () => {
  const [pairs, setPairs] = useState<SkillProximityPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [computing, setComputing] = useState(false);

  const loadPairs = async (status?: string) => {
    setLoading(true);
    try {
      const data = await fetchProximityPairs(status || undefined);
      setPairs(data);
    } catch (err) {
      console.error('Failed to load proximity pairs', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPairs(statusFilter === 'all' ? undefined : statusFilter);
  }, [statusFilter]);

  const handleAction = async (id: string, action: 'confirmed' | 'merged' | 'dismissed') => {
    setActionInProgress(id);
    try {
      await updateProximityPairStatus(id, action);
      setPairs((prev) => prev.map((p) =>
        p.id === id ? { ...p, status: action, reviewedAt: new Date().toISOString() } : p
      ));
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleComputeProximity = async () => {
    setComputing(true);
    try {
      const { data, error } = await supabase.rpc('compute_skill_proximity_pairs', { threshold: 0.3 });
      if (error) {
        alert(`Erreur: ${error.message}`);
      } else {
        alert(`Analyse terminée : ${data} paire(s) détectée(s)`);
        loadPairs(statusFilter === 'all' ? undefined : statusFilter);
      }
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setComputing(false);
    }
  };

  const pendingCount = pairs.filter((p) => p.status === 'pending').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Proximité / Redondances</h1>
          <p className="text-sm text-gray-500 mt-1">
            Paires de compétences similaires détectées — {pendingCount} en attente de review
          </p>
        </div>
        <button
          onClick={handleComputeProximity}
          disabled={computing}
          className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {computing ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Analyse en cours...
            </>
          ) : (
            'Relancer l\'analyse'
          )}
        </button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        {['all', 'pending', 'confirmed', 'merged', 'dismissed'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              statusFilter === s
                ? 'bg-[#00244F] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {s === 'all' ? 'Toutes' : STATUS_LABELS[s]?.label || s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
        </div>
      ) : pairs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="text-4xl mb-3">✓</div>
          <p className="text-sm text-gray-500">Aucune paire dans cette catégorie</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pairs.map((pair) => (
            <div key={pair.id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-4">
                {/* Skill A */}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-900 block truncate">{pair.skillA.nameFr}</span>
                  <span className="text-xs text-gray-400 font-mono">{pair.skillA.code}</span>
                </div>

                {/* Similarity badge */}
                <div className="flex flex-col items-center gap-1">
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                    pair.similarity > 0.8 ? 'bg-red-100 text-red-700' :
                    pair.similarity > 0.6 ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {(pair.similarity * 100).toFixed(0)}%
                  </div>
                  <span className="text-[10px] text-gray-400">similarité</span>
                </div>

                {/* Skill B */}
                <div className="flex-1 min-w-0 text-right">
                  <span className="text-sm font-medium text-gray-900 block truncate">{pair.skillB.nameFr}</span>
                  <span className="text-xs text-gray-400 font-mono">{pair.skillB.code}</span>
                </div>
              </div>

              {/* Status + Actions */}
              <div className="mt-4 flex items-center justify-between">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${STATUS_LABELS[pair.status]?.color || ''}`}>
                  {STATUS_LABELS[pair.status]?.label || pair.status}
                </span>

                {pair.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleAction(pair.id, 'confirmed')}
                      disabled={actionInProgress === pair.id}
                      className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-40"
                    >
                      Confirmer (garder les 2)
                    </button>
                    <button
                      onClick={() => handleAction(pair.id, 'merged')}
                      disabled={actionInProgress === pair.id}
                      className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-40"
                    >
                      Fusionner
                    </button>
                    <button
                      onClick={() => handleAction(pair.id, 'dismissed')}
                      disabled={actionInProgress === pair.id}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-40"
                    >
                      Ignorer
                    </button>
                  </div>
                )}

                {pair.reviewedAt && (
                  <span className="text-[10px] text-gray-400">
                    Revu le {new Date(pair.reviewedAt).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SkillProximity;
